import React, { useState, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Building2 } from 'lucide-react';
import type { SignUpFormData, SignUpFormErrors, SignUpPageProps, ApiResponse } from '../../types';
import PersonalDataFields, { isValidEmailFormat, validatePersonalData } from './PersonalDataFields';
import OtpVerificationStep from './OtpVerificationStep';
import BusinessProfileSelector, { BusinessProfileValue } from '@/components/auth/BusinessProfileSelector';

// ─────────────────────────────────────────────────────────────────────────────
// Step definitions: 'form' → 'business' → 'otp'
// ─────────────────────────────────────────────────────────────────────────────
type RegistrationStep = 'form' | 'business' | 'otp';

const STEPS = [
  { id: 'form', label: 'Tus datos', number: 1 },
  { id: 'business', label: 'Tu negocio', number: 2 },
  { id: 'otp', label: 'Verificación', number: 3 },
] as const;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const initialFormData: SignUpFormData = {
  email: '',
  first_name: '',
  last_name: '',
  phone: '',
  role: 'operator',
  business_sector: '',
  custom_sector: '',
  use_cases: [],
  custom_use_case: '',
};

const initialBusinessProfile: BusinessProfileValue = {
  business_sector: '',
  custom_sector: undefined,
  use_cases: [],
  custom_use_case: undefined,
};

// ─────────────────────────────────────────────────────────────────────────────
// StepIndicator sub-component
// ─────────────────────────────────────────────────────────────────────────────
const StepIndicator: React.FC<{ currentStep: RegistrationStep }> = ({ currentStep }) => {
  const currentIndex = STEPS.findIndex(s => s.id === currentStep);
  return (
    <div className="flex items-center justify-center gap-0 mb-2">
      {STEPS.map((step, idx) => {
        const isDone = idx < currentIndex;
        const isActive = idx === currentIndex;
        return (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <div
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-300
                  ${isDone ? 'bg-[#1CB3B3] border-[#1CB3B3] text-white' :
                    isActive ? 'bg-white border-[#1CB3B3] text-[#1CB3B3]' :
                    'bg-white/20 border-white/40 text-white/60'}
                `}
              >
                {isDone ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : step.number}
              </div>
              <span className={`text-xs mt-1 font-medium transition-colors ${isActive ? 'text-white' : isDone ? 'text-blue-200' : 'text-white/50'}`}>
                {step.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`h-0.5 w-12 mx-1 mt-[-12px] rounded transition-all duration-300 ${isDone ? 'bg-[#1CB3B3]' : 'bg-white/20'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main SignUpPage
// ─────────────────────────────────────────────────────────────────────────────

const SignUpPage: React.FC<SignUpPageProps> = ({ onSwitchToLogin }) => {
  const { register, sendOTP, verifyOTP } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<SignUpFormData>(initialFormData);
  const [errors, setErrors] = useState<SignUpFormErrors>({});
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [registrationStep, setRegistrationStep] = useState<RegistrationStep>('form');
  const [otpCode, setOtpCode] = useState('');
  const [businessProfile, setBusinessProfile] = useState<BusinessProfileValue>(initialBusinessProfile);
  const [businessErrors, setBusinessErrors] = useState<{
    business_sector?: string;
    custom_sector?: string;
    use_cases?: string;
    custom_use_case?: string;
  }>({});

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateEmail = isValidEmailFormat;
  // Debounce ref for fire-and-forget email capture
  const emailCaptureDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCapturedEmailRef = useRef<string>('');


  const handleInputChange = useCallback((field: keyof SignUpFormData, value: string): void => {
  /** Fire-and-forget: saves the email in the backend so a reminder can be sent if signup is abandoned */
  const captureEmailSilently = (email: string): void => {
    if (emailCaptureDebounceRef.current) {
      clearTimeout(emailCaptureDebounceRef.current);
    }
    emailCaptureDebounceRef.current = setTimeout(() => {
      if (email === lastCapturedEmailRef.current) return; // Already captured this exact email
      lastCapturedEmailRef.current = email;
      fetch(`${BACKEND_URL}/api/auth/capture-email`, {
        method: 'POST',
        credentials: 'include', // campaign cookies for the Meta Lead event
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      }).catch(() => {}); // Silent fail — never shown to user
    }, 800); // 800 ms debounce to avoid calling on every keystroke
  };

    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
    if (field === 'email') {
      const valid = validateEmail(value);
      setIsValidEmail(valid);
      if (valid) {
        captureEmailSilently(value);
      }
    }
  }, [errors]);

  const validatePersonalForm = (): boolean => {
    const newErrors: SignUpFormErrors = validatePersonalData(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateBusinessProfile = (): boolean => {
    const newErrors: typeof businessErrors = {};

    if (!businessProfile.business_sector) {
      newErrors!.business_sector = 'Selecciona el sector de tu negocio';
    }

    // Si eligió "Otro sector", el texto es obligatorio
    if (businessProfile.business_sector === '__sector_otro__' && !businessProfile.custom_sector?.trim()) {
      newErrors!.custom_sector = 'Por favor describe tu sector';
    }

    const totalUses = (businessProfile.use_cases?.length ?? 0);
    const hasOther = Boolean(businessProfile.custom_use_case?.trim());

    // Solo pedir usos si ya hay sector seleccionado
    if (businessProfile.business_sector && totalUses === 0 && !hasOther) {
      newErrors!.use_cases = 'Selecciona al menos un uso o describe el tuyo';
    }

    // Si eligió "Otro sector", el USO también es obligatorio y debe describirse
    if (businessProfile.business_sector === '__sector_otro__' && !businessProfile.custom_use_case?.trim()) {
      newErrors!.custom_use_case = 'Por favor describe la funcionalidad que necesitas';
    }

    setBusinessErrors(newErrors);
    return Object.keys(newErrors ?? {}).length === 0;
  };

  // ── Step 1: Personal form → go to business step ────────────────────────────
  const handlePersonalSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!validatePersonalForm()) {
      toast.error('Por favor corrige los errores en el formulario');
      return;
    }
    setRegistrationStep('business');
  };

  // ── Step 2: Business profile → register & send OTP ────────────────────────
  const handleBusinessSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!validateBusinessProfile()) {
      toast.error('Completa la información de tu negocio');
      return;
    }

    setLoading(true);

    // Resuelve el sector real: si eligió "Otro" usa el texto personalizado
    const resolvedSector = businessProfile.business_sector === '__sector_otro__'
      ? (businessProfile.custom_sector?.trim() ?? 'Otro')
      : businessProfile.business_sector;

    const registerPayload = {
      ...formData,
      business_sector: resolvedSector,
      use_cases: businessProfile.use_cases,
      custom_use_case: businessProfile.custom_use_case?.trim() || undefined,
    };

    const result: ApiResponse<any> = await register(registerPayload);
    
    if (!result.success) {
      setLoading(false);
      if (result.error?.includes('already exists')) {
        toast.error('Usuario ya existe', {
          description: 'Este email ya está registrado. ¿Quieres iniciar sesión?'
        });
        setErrors({ email: 'Este email ya está registrado' });
        setRegistrationStep('form');
      } else {
        toast.error('Error al crear cuenta', {
          description: result.error || 'Error desconocido'
        });
      }
      return;
    }

    // Send OTP automatically
    const otpResult = await sendOTP(formData.email);
    setLoading(false);

    if (otpResult.success) {
      setRegistrationStep('otp');
      toast.success('¡Cuenta creada exitosamente!', {
        description: 'Hemos enviado un código de verificación a tu email'
      });
    } else {
      toast.error('Cuenta creada pero error al enviar código', {
        description: 'Por favor inicia sesión manualmente'
      });
      onSwitchToLogin();
    }
  };

  // ── Step 3: Verify OTP ─────────────────────────────────────────────────────
  const handleVerifyOTP = async (): Promise<void> => {
    if (otpCode.length !== 6) {
      toast.error('El código debe tener 6 dígitos');
      return;
    }
    setLoading(true);
    const result: ApiResponse<{ user: any }> = await verifyOTP(formData.email, otpCode);
    setLoading(false);
    if (result.success) {
      toast.success('¡Bienvenido a Reveca!', {
        description: 'Tu cuenta ha sido verificada exitosamente'
      });
    } else {
      toast.error('Código incorrecto', { description: result.error || 'Código inválido' });
      setOtpCode('');
    }
  };

  const handleResendOTP = async (): Promise<void> => {
    setLoading(true);
    const result: ApiResponse<any> = await sendOTP(formData.email);
    setLoading(false);
    if (result.success) {
      toast.success('Nuevo código enviado');
      setOtpCode('');
    } else {
      toast.error('Error al reenviar código');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E8F7F7] via-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] px-8 py-6 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-3xl">🚀</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Crear Cuenta</h1>
          <p className="text-blue-100 text-sm mb-4">
            Únete a Reveca y automatiza tus campañas de WhatsApp
          </p>
          <StepIndicator currentStep={registrationStep} />
        </div>

        {/* Content */}
        <div className="px-8 py-8">
          {/* ── STEP 1: Personal data ───────────────────────────────────── */}
          {registrationStep === 'form' && (
            <form onSubmit={handlePersonalSubmit} className="space-y-5">
              <PersonalDataFields
                values={formData}
                errors={errors}
                onChange={(field, value) => handleInputChange(field as keyof SignUpFormData, value)}
                disabled={loading}
              />

              {/* Submit */}
              <button
                type="submit"
                id="signup-next-btn"
                disabled={!isValidEmail || !formData.first_name || !formData.last_name || !formData.phone || loading}
                className="w-full bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white py-3 px-6 rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 text-base shadow-lg hover:shadow-xl"
              >
                <span>Continuar</span>
                <ArrowRight className="h-5 w-5" />
              </button>

              {/* Switch to login */}
              <div className="mt-2 pt-5 border-t border-gray-100 text-center">
                <p className="text-sm text-gray-500 mb-3 font-medium">Si ya tienes una cuenta:</p>
                <button
                  type="button"
                  onClick={onSwitchToLogin}
                  disabled={loading}
                  className="w-full bg-white border-2 border-[#1CB3B3] text-[#1CB3B3] py-3 px-6 rounded-lg font-bold hover:bg-[#E8F7F7] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 shadow-md group"
                >
                  <span>Inicia sesión aquí</span>
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </form>
          )}

          {/* ── STEP 2: Business profile ────────────────────────────────── */}
          {registrationStep === 'business' && (
            <form onSubmit={handleBusinessSubmit} className="space-y-6">
              {/* Header hint */}
              <div className="flex items-center gap-2 p-3 bg-[#E8F7F7] rounded-xl border border-[#1CB3B3]/30">
                <Building2 className="w-5 h-5 text-[#1CB3B3] flex-shrink-0" />
                <p className="text-sm text-gray-700">
                  Cuéntanos sobre tu negocio para personalizar tu experiencia
                </p>
              </div>

              <BusinessProfileSelector
                value={businessProfile}
                onChange={setBusinessProfile}
                errors={businessErrors}
                disabled={loading}
              />

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setRegistrationStep('form')}
                  disabled={loading}
                  className="flex-1 bg-white border-2 border-gray-200 text-gray-600 py-3 px-4 rounded-lg font-medium hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Atrás</span>
                </button>
                <button
                  type="submit"
                  id="signup-create-account-btn"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white py-3 px-6 rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Creando cuenta...</span>
                    </>
                  ) : (
                    <>
                      <span>Crear cuenta</span>
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* ── STEP 3: OTP Verification ────────────────────────────────── */}
          {registrationStep === 'otp' && (
            <OtpVerificationStep
              email={formData.email}
              code={otpCode}
              onCodeChange={setOtpCode}
              onSubmit={handleVerifyOTP}
              onResend={handleResendOTP}
              loading={loading}
            >
              <p className="text-sm text-gray-600">
                ¿Ya tienes cuenta?{' '}
                <button
                  type="button"
                  onClick={onSwitchToLogin}
                  disabled={loading}
                  className="text-[#1CB3B3] hover:text-[#0D7377] font-medium"
                >
                  Inicia sesión aquí
                </button>
              </p>
            </OtpVerificationStep>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-8 py-4 text-center">
          <p className="text-xs text-gray-500">
            Al crear una cuenta, aceptas nuestros términos de servicio
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;